import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DogIcon as Dog, PlusIcon as Plus, MagnifyingGlassIcon as MagnifyingGlass, GenderMaleIcon as GenderMale, GenderFemaleIcon as GenderFemale, GitForkIcon as GitFork, ArrowRightIcon as ArrowRight, IdentificationBadgeIcon as IdentificationBadge } from "@phosphor-icons/react";
import { formatDate, thumbUrl } from "@/lib/utils-app";
import EmptyState from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/SkeletonList";
import DogCardDeleteButton from "@/components/DogCardDeleteButton";

export default function Dogs() {
  // Honor `?sex=male|female|all` and `?breed=X` from the URL so the
  // dashboard stat cards can deep-link straight into a filtered view.
  const [searchParams] = useSearchParams();
  const urlSex = searchParams.get("sex");
  const urlBreed = searchParams.get("breed") || "";
  const [dogs, setDogs] = useState([]);
  const [search, setSearch] = useState("");
  const [breed, setBreed] = useState(urlBreed);
  const [sex, setSex] = useState(urlSex && ["male", "female"].includes(urlSex) ? urlSex : "all");
  const [loading, setLoading] = useState(true);
  const [passports, setPassports] = useState({});

  useEffect(() => {
    api.get("/passports/mine").then((r) => setPassports(r.data || {})).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (breed) params.breed = breed;
    if (sex && sex !== "all") params.sex = sex;
    api
      .get("/dogs", { params })
      .then((r) => setDogs(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sex, breed]);

  const onFilter = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Registry</p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">My dogs</h1>
          <p className="text-[13px] text-[#6B6B6B] mt-2 max-w-xl">
            Dogs currently registered to your account. Sold a pup? Use{" "}
            <Link to="/transfers" className="underline hover:text-ink">Transfers</Link>{" "}
            to officially hand off ownership.
          </p>
        </div>
        <Link to="/dogs/new">
          <Button className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11" data-testid="add-dog-btn">
            <Plus size={16} className="mr-1" /> Register dog
          </Button>
        </Link>
      </div>

      <form
        onSubmit={onFilter}
        className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3"
      >
        <div className="relative md:col-span-2">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <Input
            placeholder="Search by name, reg #, microchip"
            className="pl-9 rounded-lg"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="dogs-search-input"
          />
        </div>
        <Input
          placeholder="Breed"
          className="rounded-lg"
          value={breed}
          onChange={(e) => setBreed(e.target.value)}
          data-testid="dogs-breed-input"
        />
        <div className="flex gap-2">
          <Select value={sex} onValueChange={setSex}>
            <SelectTrigger data-testid="dogs-sex-select">
              <SelectValue placeholder="Sex" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sexes</SelectItem>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="submit"
            variant="outline"
            className="rounded-full border-[#E5E5E5]"
            data-testid="dogs-filter-btn"
          >
            Filter
          </Button>
        </div>
      </form>

      {loading ? (
        <CardGridSkeleton count={6} testId="dogs-loading-skeleton" />
      ) : dogs.length === 0 ? (
        <EmptyState
          testId="dogs-empty"
          icon={<Dog size={32} weight="duotone" />}
          title="No dogs yet"
          description="Register your first dog to start building your pedigree, request transfers, and unlock kennel features."
          ctaLabel="Register a dog"
          ctaTo="/dogs/new"
          secondaryLabel="Browse marketplace"
          secondaryTo="/marketplace"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {dogs.map((d) => (
            <Link
              key={d.id}
              to={`/dogs/${d.id}`}
              className="group relative bg-white border border-[#EAEAEA] hover:border-gold/55 active:scale-[0.99] rounded-xl overflow-hidden shadow-luxury-light hover:shadow-gold-glow hover:-translate-y-0.5 transition-all duration-500"
              data-testid={`dog-card-${d.id}`}
            >
              {/* Inline delete — top-right of the card. Stops Link
                  propagation. Lets breeders kill duplicate puppies
                  without diving 3 navigations deep (Janis-bug 2026-06). */}
              <div className="absolute top-2 right-2 z-10">
                <DogCardDeleteButton
                  dogId={d.id}
                  dogName={d.name}
                  registrationNumber={d.registration_number}
                  size="compact"
                  onDeleted={() => setDogs((prev) => prev.filter((x) => x.id !== d.id))}
                />
              </div>
              <div className="aspect-[4/3] bg-gradient-to-br from-[#FBF7EC] via-[#F3E9C7]/40 to-[#FBF7EC] overflow-hidden">
                {d.photos?.[0] ? (
                  <img
                    src={thumbUrl(d.photos[0])}
                    alt={d.name}
                    className="w-full h-full object-cover photo-luxury group-hover:scale-[1.02] transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white border border-[#C9A227]/40 shadow-[0_4px_12px_rgba(193,162,39,0.10)]">
                      <Dog size={26} weight="duotone" className="text-[#C9A227]" />
                    </span>
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-spectral text-xl tracking-tight group-hover:text-crimson transition-colors">{d.name}</h3>
                  {d.sex === "male" ? (
                    <GenderMale size={20} className="text-ink mt-1" />
                  ) : (
                    <GenderFemale size={20} className="text-crimson mt-1" />
                  )}
                </div>
                <p className="text-sm text-[#6B6B6B] mt-1">{d.breed}</p>
                <div className="gold-hairline mt-3" />
                <div className="flex items-center justify-between mt-3 text-xs text-[#6B6B6B] font-tabular">
                  <span>{d.dob ? `Born ${formatDate(d.dob)}` : "DOB unknown"}</span>
                  {d.registration_number && (
                    <span className="font-mono">{d.registration_number}</span>
                  )}
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/dogs/${d.id}/passport`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = `/dogs/${d.id}/passport`;
                    }
                  }}
                  data-testid={`dog-card-passport-${d.id}`}
                  className={`mt-3 w-full inline-flex items-center justify-between gap-2 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors cursor-pointer border ${
                    (passports[d.id]?.overall ?? 0) >= 70
                      ? "text-[#0F8043] bg-[#0F8043]/5 border-[#0F8043]/25 hover:bg-[#0F8043]/10"
                      : "text-[#8A6A00] bg-[#FBF7EC] border-[#E5D08A] hover:bg-[#F5EBCC]"
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <IdentificationBadge size={13} weight="duotone" />
                    Care Passport · {passports[d.id]?.overall ?? 0}%
                  </span>
                  <ArrowRight size={12} weight="bold" />
                </div>
                {d.titles?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {d.titles.slice(0, 3).map((t) => (
                      <span
                        key={t.id}
                        className="bg-[#C1272D]/10 text-crimson border border-[#C1272D]/20 rounded-full px-2 py-0.5 text-xs font-semibold"
                      >
                        {t.title}
                      </span>
                    ))}
                  </div>
                )}
                {(!d.sire_id || !d.dam_id) && (
                  <div className="mt-3 pt-3 border-t border-[#E5E5E5]/70">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = `/pedigree/${d.id}`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          window.location.href = `/pedigree/${d.id}`;
                        }
                      }}
                      data-testid={`dog-card-backfill-${d.id}`}
                      className="w-full inline-flex items-center justify-between gap-2 text-xs font-semibold text-[#8A6A00] bg-[#FBF7EC] hover:bg-[#F5EBCC] border border-[#E5D08A] rounded-full px-3 py-1.5 transition-colors cursor-pointer"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <GitFork size={13} weight="duotone" />
                        {!d.sire_id && !d.dam_id
                          ? "Pedigree empty — add parents"
                          : !d.sire_id
                          ? "Missing sire — add ancestor"
                          : "Missing dam — add ancestor"}
                      </span>
                      <ArrowRight size={12} weight="bold" />
                    </div>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  );
}
